'use client';

import { useState, useEffect } from 'react';
import * as Styled from './styled';
import { IRepository } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { IEvaluation } from '@/lib/evaluationsApi';
import { TEvaluationModel } from '@/lib/evaluationModelsApi';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import Flash from '@/ui/Flash';

import SetupForm from './SetupForm';
import RunForm from './RunForm';
import ReadOnlyView from './ReadOnlyView';
import SchemaRail from './SchemaRail';

export type IEvaluationFormData = IEvaluation & { type: string };

type EvaluationWizardProps = {
  repository: IRepository;
  evaluation: IEvaluation;
  availableModels: TEvaluationModel[];
  schema?: IDataFrameSchema;
  error?: IResponseError;
  onRunSample: (data: IEvaluationFormData) => void;
  onRunEvaluation: (data: IEvaluationFormData) => void;
};

const EvaluationWizard: React.FC<EvaluationWizardProps> = ({
  repository,
  evaluation: initialEvaluation,
  availableModels,
  schema: initialSchema,
  error,
  onRunSample,
  onRunEvaluation,
}) => {
  const [evaluation, setEvaluation] = useState<IEvaluation>(initialEvaluation);
  const [schema, setSchema] = useState<IDataFrameSchema | undefined>(initialSchema);
  const [extractedFields, setExtractedFields] = useState<string[]>([]);

  const isSampleRun = evaluation.is_sample === true;
  const isEvaluationRun = !isSampleRun;
  const isFullyProcessed = evaluation.progress?.total === evaluation.progress?.processed;
  const hasCommit = !!evaluation.closing_commit;
  const maybeIsCompletedRun = evaluation.status === 'completed' || hasCommit || isFullyProcessed;

  const repositoryAccessLevel = repository.access_level;
  const hasReadOnlyAccess = repositoryAccessLevel === 'viewer';
  const isReadOnlyView = isEvaluationRun && ['queued', 'started', 'completed', 'error'].includes(evaluation.status);

  const evaluationMode = evaluation.is_sample === false ? 'run' : 'setup';
  const [viewMode, setViewMode] = useState<'setup' | 'run'>(evaluationMode);

  useEffect(() => {
    setEvaluation(initialEvaluation);
  }, [initialEvaluation]);

  const runSample = (data: IEvaluation) => {
    setEvaluation(data as unknown as IEvaluation);
    onRunSample(data as unknown as IEvaluationFormData);
  };

  const onNext = (data: IEvaluation) => {
    setViewMode('run');
    setEvaluation(data as unknown as IEvaluation);
  };

  const runEvaluation = (data: IEvaluationFormData) => {
    setEvaluation(data as unknown as IEvaluation);
    onRunEvaluation(data);
  };

  return (
    <Styled.EvaluationWizard data-tutorialid="evaluation-wizard">
      {error && (
        <Flash.Danger style={{ marginBottom: 20 }}>
          <b>Error: </b>
          {error.title}, {error.detail}
        </Flash.Danger>
      )}
      {isReadOnlyView || hasReadOnlyAccess ? (
        <ReadOnlyView repository={repository} evaluation={evaluation} />
      ) : viewMode === 'setup' ? (
        <SetupForm
          data-tutorialid="evaluation-wizard-setup-form"
          evaluation={evaluation}
          availableModels={availableModels}
          schema={schema as IDataFrameSchema}
          onRunSample={runSample}
          onNext={onNext}
        ></SetupForm>
      ) : (
        <RunForm
          data-tutorialid="evaluation-wizard-run-form"
          evaluation={evaluation}
          availableModels={availableModels}
          schema={schema as IDataFrameSchema}
          onRun={runEvaluation}
          onBack={() => setViewMode('setup')}
        >
          <SchemaRail
            data-tutorialid="evaluation-wizard-schema-rail"
            schema={schema as IDataFrameSchema}
            extractedFields={[]}
          />
        </RunForm>
      )}
    </Styled.EvaluationWizard>
  );
};

export default EvaluationWizard;
